import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { User } from 'src/trpc';
import { Pencil } from 'lucide-react';
import { PatternFormat } from 'react-number-format';

interface EditCellProps {
  user: User;
  onEdit: (user: User) => void;
}

const EditCell: React.FC<EditCellProps> = ({ user, onEdit }) => {
  return (
    <Button variant="ghost" onClick={() => onEdit(user)}>
      <Pencil className="h-4 w-4" />
    </Button>
  );
};

const PhoneCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  if (!value || value === '') return <span>-</span>;

  // If the number already has a country code with +, use it directly
  if (value.startsWith('+')) {
    return (
      <PatternFormat
        value={value.substring(1)} // Remove the + since format adds it
        displayType="text"
        format="+# (###) ###-####"
      />
    );
  }

  // Otherwise, clean the number and add country code if needed
  const digits = value.replace(/\D/g, '');
  const formattedNumber = digits.length === 10 ? `1${digits}` : digits;

  return <PatternFormat value={formattedNumber} displayType="text" format="+# (###) ###-####" />;
};

export const createUserColumns = (onEdit: (user: User) => void): ColumnDef<User>[] => [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => {
      const firstName = row.original.name?.first;
      const lastName = row.original.name?.last;
      if (!firstName && !lastName) return <span>""</span>;
      return `${firstName || ''} ${lastName || ''}`.trim();
    },
  },
  {
    accessorKey: 'email',
    header: 'Email',
  },
  {
    accessorKey: 'phone',
    header: 'Phone',
    cell: ({ row }) => <PhoneCell value={row.original.phone} />,
  },
  {
    accessorKey: 'roles',
    header: 'Roles',
    cell: ({ row }) => row.original.roles?.join(', ') || '',
  },
  {
    id: 'actions',
    header: 'Edit',
    cell: ({ row }) => <EditCell user={row.original} onEdit={onEdit} />,
  },
];
